import React from "react";
import { ShieldCheck, Wand2, Wrench, Clock, CheckCircle2 } from "lucide-react";

const POINTS = [
  {
    icon: ShieldCheck,
    title: "100% Satisfaction Guarantee",
    desc: "If a streak shows up after we leave, we come back. No excuses, no fees.",
  },
  {
    icon: Wand2,
    title: "Right tool for the job",
    desc: "Water-fed pole as the standard for safe, streak-free results — and we adapt our method on-site to whatever your windows actually need.",
  },
  {
    icon: Wrench,
    title: "Detail-obsessed",
    desc: "Frames, sills, and tracks are part of every job. Not an upsell.",
  },
  {
    icon: Clock,
    title: "Flexible scheduling",
    desc: "Open 8 AM to 8 PM, daily — including weekends across greater Baton Rouge.",
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="relative rounded-[28px] overflow-hidden ring-1 ring-blue-100 vgc-card-shadow bg-white p-7 lg:p-9">
            <div
              aria-hidden
              className="absolute -top-24 -right-20 h-72 w-72 rounded-full opacity-50"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,180,216,0.35), rgba(0,180,216,0) 65%)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-[#0F4C81]/8 ring-1 ring-[#0F4C81]/15 text-xs font-bold text-[#0F4C81] uppercase tracking-[0.18em]">
                Trained · Local
              </div>
              <h3 className="mt-5 font-[Outfit] font-extrabold text-[#0B2542] text-3xl leading-tight">
                Built right here in Baton Rouge.
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Family-friendly, neighbor-recommended, and obsessive about the details.
                Every job ends with a final walkthrough — and our guarantee in writing.
              </p>

              <ul className="mt-7 space-y-3.5">
                {[
                  "Locally owned and operated",
                  "Eco-friendly purified water rinse",
                  "Same-week appointments available",
                  "Residential & commercial",
                ].map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-slate-800"
                    data-testid={`why-fact-${i}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                    <span className="text-[15px]">{line}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-7 border-t border-blue-50 flex items-center justify-between gap-3">
                <div>
                  <div className="text-2xl font-[Outfit] font-extrabold text-[#0F4C81] leading-none">
                    100%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Satisfaction guarantee</div>
                </div>
                <div>
                  <div className="text-2xl font-[Outfit] font-extrabold text-[#0F4C81] leading-none">
                    8–8
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Daily availability</div>
                </div>
                <div>
                  <div className="text-2xl font-[Outfit] font-extrabold text-[#0F4C81] leading-none">
                    5★
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Customer rated</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#00B4D8] font-bold">
            Why Vision Glass Care
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-[#0B2542] leading-tight">
            Built on detail, backed by guarantee.
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            We're a Baton Rouge crew that treats every home and business like our own.
            Our reputation is in the details — the corners, the tracks, the trim.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`why-point-${i}`}
                  className="rounded-2xl bg-white border border-blue-100 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00B4D8]/50 hover:shadow-[0_12px_28px_rgba(15,76,129,0.08)]"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#0F4C81]/8 text-[#0F4C81] flex items-center justify-center mb-4 ring-1 ring-[#0F4C81]/10">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-[Outfit] font-bold text-[#0B2542] text-lg leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
